import CustomTooltip from '../../../../../shared/CustomTooltip';
import { Box, Chip } from '@mui/material';
import type { FilterOptionsState } from '@mui/material/useAutocomplete';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useGetAllExternalAttendeesQuery } from '../../../../../../app/Slices/SevenDayApiSlice';
import CustomAutocomplete from '../../../../../../layout/styledcomponents/CustomAutoComplete';
import UserRole from '../../../../../../shared/AuthRoles';
import { ExperienceType } from '../../../../../enums/experience';
import { User } from '../../../../../types/user';
import {
    dedupeByEmail,
    defaultAutocompleteFilter,
    getAutocompleteOptionEmail,
    isValidEmail,
    pickedEmails,
    toSelected,
} from './AttendeesSection';

import { HelpOutline } from '@mui/icons-material';
import { List, ListItem, Typography } from '@mui/material';
import CustomInputLabel from '../../../../../../layout/styledcomponents/CustomInputLabel';
import { AutocompleteStaffOption, enforceMaxOnChange } from './StaffAttendees';

export type StaffOption = {
    value: User;
    label: string;
    teamId: number;
    teamName: string;
};

interface AttendeesProps {
    experienceType: ExperienceType;
    guestCount: number;
    usesFaceRec: boolean;
    isEditing: boolean;
}

const ExternalAttendees = ({
    experienceType,
    guestCount,
    usesFaceRec,
    isEditing,
}: AttendeesProps) => {
    const loggedInUser = UserRole();
    const { control, watch } = useFormContext();
    const [guestInputValue, setGuestInputValue] = useState('');

    const { data: externalAttendees } = useGetAllExternalAttendeesQuery(
        loggedInUser?.company,
    );

    const adults = watch('bookingInfo.adults');
    const staffAttending = watch('staffAttending') || [];
    const externalAttending = watch('externalAttendees') || [];
    const rsvpEnabled = Boolean(watch('rsvpConfig.rsvpEnabled'));

    const maxAttendees =
        experienceType === ExperienceType.Commercial
            ? guestCount
            : experienceType === ExperienceType.Activity
              ? guestCount
              : adults;

    const currentAttendeeCount =
        (staffAttending?.length || 0) + (externalAttending?.length || 0);
    const canAddMore = currentAttendeeCount < maxAttendees && maxAttendees > 0;

    const externalGuestOptions =
        externalAttendees?.map((user: User) => ({
            value: user,
            label: user.firstName
                ? `${user.firstName} ${user.lastName} (${user.email})`
                : user.email,
        })) || [];

    useEffect(() => {
        if (!canAddMore) {
            setGuestInputValue('');
        }
    }, [canAddMore]);

    const preventAddKeysWhenFull = (e: any) => {
        if (!canAddMore && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const addBySpace = (
        e: any,
        inputValue: string,
        setInputValue: (v: string) => void,
        current: Array<User | { email: string }>,
        onChange: (v: any) => void,
        canAddMore: boolean,
    ) => {
        if (e.key !== ' ') return;

        const email = String(inputValue || '').trim();
        if (!email) return;

        if (!isValidEmail(email)) {
            e.preventDefault();
            return;
        }

        if (!canAddMore) {
            e.preventDefault();
            return;
        }

        e.preventDefault();
        onChange(dedupeByEmail([...current, { email }]));
        setInputValue('');
    };

    return (
        <Box>
            {!isEditing && (
                <CustomInputLabel
                    required={rsvpEnabled}
                    htmlFor='guest-attendees__input'
                >
                    Guest Attendees
                    {!canAddMore && (
                        <Typography
                            component='span'
                            sx={{
                                ml: 1,
                                fontSize: '11px',
                                fontWeight: 500,
                                color: 'error.main',
                            }}
                        >
                            Max attendees reached
                        </Typography>
                    )}
                    <CustomTooltip
                        title={
                            <Typography>
                                <List>
                                    <ListItem>
                                        You can invite people outside your
                                        organization to attend the experience.
                                    </ListItem>

                                    <ListItem>
                                        {usesFaceRec
                                            ? 'Note: This experience uses facial recognition for entry.'
                                            : 'Note: If attendees are not selected, they won’t receive this reservation in their 7DAY account.'}
                                    </ListItem>
                                </List>
                            </Typography>
                        }
                        placement='right'
                    >
                        <HelpOutline
                            sx={{
                                ml: 0.5,
                                color: 'primary.main',
                                fontSize: '14px',
                            }}
                        />
                    </CustomTooltip>
                </CustomInputLabel>
            )}
            <Controller
                name='externalAttendees'
                control={control}
                render={({
                    field: { onChange, value },
                    fieldState: { error },
                }) => {
                    const current: Array<User | { email: string }> =
                        Array.isArray(value) ? value : [];

                    return (
                        <CustomAutocomplete<any, true, false, true>
                            id='guest-attendees__input'
                            multiple
                            freeSolo
                            disableCloseOnSelect={
                                maxAttendees - currentAttendeeCount !== 1
                            }
                            filterSelectedOptions
                            options={externalGuestOptions}
                            value={toSelected(current)}
                            inputValue={guestInputValue}
                            onInputChange={(_, v) => setGuestInputValue(v)}
                            onKeyDown={(e: any) => {
                                preventAddKeysWhenFull(e);
                                addBySpace(
                                    e,
                                    guestInputValue,
                                    setGuestInputValue,
                                    current,
                                    onChange,
                                    canAddMore,
                                );
                            }}
                            getOptionLabel={(o: any) => {
                                if (typeof o === 'string') return o;
                                if (o?.inputValue) return o.inputValue;
                                return o?.label ?? '';
                            }}
                            isOptionEqualToValue={(o: any, v: any) => {
                                const oEmail = (
                                    o?.value?.email ||
                                    o?.inputValue ||
                                    ''
                                ).toLowerCase();
                                const vEmail = (
                                    v?.value?.email ||
                                    v?.inputValue ||
                                    ''
                                ).toLowerCase();
                                return !!oEmail && oEmail === vEmail;
                            }}
                            filterOptions={(
                                options: AutocompleteStaffOption[],
                                params: FilterOptionsState<AutocompleteStaffOption>,
                            ) => {
                                const input = params.inputValue?.trim() || '';
                                const picked = pickedEmails(current);
                                const byInput = defaultAutocompleteFilter(
                                    options,
                                    params,
                                );
                                const filtered = byInput.filter(
                                    (opt: AutocompleteStaffOption) => {
                                        const email =
                                            getAutocompleteOptionEmail(opt);
                                        return !picked.includes(email);
                                    },
                                );

                                if (canAddMore && isValidEmail(input)) {
                                    const inputEmail = input.toLowerCase();
                                    const matchesExistingOption = filtered.some(
                                        (opt: AutocompleteStaffOption) =>
                                            (
                                                getAutocompleteOptionEmail(
                                                    opt,
                                                ) || ''
                                            ).toLowerCase() === inputEmail,
                                    );
                                    if (!matchesExistingOption) {
                                        filtered.push({
                                            inputValue: input,
                                            label: input,
                                        });
                                    }
                                }

                                return filtered;
                            }}
                            onChange={(_: any, next: any[]) => {
                                enforceMaxOnChange(
                                    next,
                                    current,
                                    onChange,
                                    () => setGuestInputValue(''),
                                    canAddMore,
                                );
                            }}
                            renderTags={(tagValue: any[], getTagProps: any) =>
                                tagValue.map((opt, index) => {
                                    const email =
                                        opt?.value?.email ||
                                        opt?.inputValue ||
                                        opt?.label ||
                                        '';
                                    const first = opt?.value?.firstName;
                                    const last = opt?.value?.lastName;
                                    const label =
                                        first || last
                                            ? `${first ?? ''} ${
                                                  last ?? ''
                                              } (${email})`.trim()
                                            : email;

                                    return (
                                        <Chip
                                            {...getTagProps({
                                                index,
                                            })}
                                            key={email || index}
                                            label={label}
                                            size='small'
                                        />
                                    );
                                })
                            }
                            getOptionDisabled={() => !canAddMore}
                            label={isEditing ? undefined : 'Guest Attendees'}
                            placeholder={
                                adults === 0 ? 'No guests attending' : ''
                            }
                            error={!!error}
                            helperText={error && error.message}
                            fontSize='12px'
                            fontWeight={500}
                        />
                    );
                }}
            />
            {!canAddMore && maxAttendees > 0 && isEditing && (
                <Typography variant='body2' color='text.secondary'>
                    {' '}
                    Max occupancy reached
                </Typography>
            )}
        </Box>
    );
};

export default ExternalAttendees;
